import React, { createContext, useContext, useState, useEffect, ReactNode, FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Account, AccountData, findAccountById } from '../utils/accountUtils';
import { setAccountGroup } from '../utils/analytics';
import { useAuth } from '../features/auth';
import { RouteSkeleton } from '../Components/Skeleton/RouteSkeleton';

// Definir la interfaz para el contexto
interface AccountContextType {
  currentAccount: Account | null;
  setAccount: (accountId: string) => void;
  isValidAccount: (accountId: string) => boolean;
}

// Crear el contexto
const AccountContext = createContext<AccountContextType | undefined>(undefined);

// Proveedor del contexto
export const AccountProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [currentAccount, setCurrentAccountState] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { accountId } = useParams();
  const { user } = useAuth();

  useEffect(() => {
    const loadCurrentAccount = async () => {
      try {
        if (accountId) {
          // Verificamos si el AccountID de la URL coincide con el del usuario autenticado
          if (user?.accountId && user.accountId !== accountId) {
            // Permitir navegación para superadmins y admins
            if (user.role !== 'superadmin' && user.role !== 'admin') {
              // Permitir si la cuenta está en la lista de cuentas permitidas (ej. rol seller)
              const isAllowed = user.allowedAccountIds?.includes(accountId);
              if (!isAllowed) {
                // Si son diferentes y no es admin/superuser ni cuenta permitida, redirigimos a la cuenta del usuario
                // Redirigir al index para que SmartRedirect maneje la ruta correcta
                navigate(`/accounts/${user.accountId}`, { replace: true });
                return;
              }
            }

          }

          // Obtenemos los detalles de la cuenta
          const accountDetails = await findAccountById(accountId);

          if (accountDetails) {
            // Convertir a formato Account
            const account: Account = {
              id: accountDetails.id,
              name: accountDetails.companyName,
              status: accountDetails.status,
              country: accountDetails.country,
              billingPlan: accountDetails.billingPlan,
            };

            setCurrentAccountState(account);

            // Attribute subsequent events to the account actually being viewed
            // (an admin may be browsing one that isn't their own), now that we
            // know its name (IMP-311). `plan` stays unset: billingPlan is a fee
            // object here, not the plan name the group contract expects.
            setAccountGroup({ id: account.id, name: account.name });
          } else if (user?.accountId) {
            // Si la cuenta de la URL no es válida pero el usuario tiene una cuenta, redirigir a esa
            // Redirigir al index para que SmartRedirect maneje la ruta correcta
            navigate(`/accounts/${user.accountId}`, { replace: true });
          } else if (window.location.pathname !== '/login') {
            // Si no hay cuenta válida, redirigir al login
            navigate('/login', { replace: true });
          }
        } else if (user?.accountId && window.location.pathname !== '/login') {
          // Si no hay AccountID en la URL pero el usuario tiene una cuenta, redirigir
          // Redirigir al index para que SmartRedirect maneje la ruta correcta
          navigate(`/accounts/${user.accountId}`, { replace: true });
        }

        setLoading(false);
      } catch (error) {

        setLoading(false);
      }
    };

    loadCurrentAccount();
  }, [accountId, navigate, user]);

  // Función para establecer la cuenta actual
  const setAccount = (accountId: string) => {
    // Recargar la página para que los efectos se ejecuten de nuevo
    window.location.reload();
  };

  // Función para verificar si una cuenta es válida
  const isValidAccount = (accountId: string) => {
    // Permitir acceso si es su propia cuenta, si es admin/superadmin, o si está en allowedAccountIds
    return accountId === user?.accountId ||
      user?.role === 'superadmin' ||
      user?.role === 'admin' ||
      (user?.allowedAccountIds?.includes(accountId) ?? false);
  };

  if (loading && window.location.pathname !== '/login') {
    return <RouteSkeleton />;
  }

  return (
    <AccountContext.Provider
      value={{
        currentAccount,
        setAccount,
        isValidAccount
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
}; 